/**
 * Chinese copy — the source of truth for the translation shape.
 * `en.ts` is typed against this object, so a missing key fails the build.
 */
export const zh = {
  common: {
    appName: 'Medaka Games Reward',
    loading: '加载中',
    save: '保存',
    saving: '保存中…',
    saved: '已保存',
    cancel: '取消',
    confirm: '确认',
    close: '关闭',
    delete: '删除',
    edit: '编辑',
    submit: '提交',
    submitting: '提交中…',
    search: '搜索',
    searchPlaceholder: '搜索昵称或 Player ID',
    filter: '筛选',
    reset: '重置',
    all: '全部',
    more: '更多',
    retry: '重试',
    back: '返回',
    next: '下一页',
    previous: '上一页',
    page: '第 {{page}} 页',
    pageOf: '第 {{page}} / {{total}} 页',
    results: '共 {{count}} 条',
    signIn: '登录',
    signUp: '注册',
    signOut: '退出登录',
    copy: '复制',
    copied: '已复制',
    points: '积分',
    pointsUnit: '分',
    rank: '排名',
    unranked: '暂无排名',
    time: '时间',
    player: '玩家',
    you: '你',
    comingSoon: '即将推出',
    optional: '可选',
    required: '必填',
    language: '语言',
    theme: '主题',
    themeDark: '深色',
    themeLight: '浅色',
    themeSystem: '跟随系统',
    justNow: '刚刚',
    minutesAgo: '{{count}} 分钟前',
    hoursAgo: '{{count}} 小时前',
    daysAgo: '{{count}} 天前',
    viewAll: '查看全部',
    empty: '这里还什么都没有',
  },
  nav: {
    home: '首页',
    achievements: '成就',
    speedruns: '速通',
    leaderboard: '排行榜',
    community: '社区',
    players: '玩家',
    profile: '个人主页',
    points: '积分',
    notifications: '通知',
    settings: '设置',
    myProfile: '我的主页',
    menu: '菜单',
    skipToContent: '跳到主要内容',
    links: '链接',
  },
  terms: {
    title: '条款',
    link: '条款',
    column: '社区规则及条款',
    subtitle: '使用本站前请阅读。管理员可在后台更新中英内容。',
    empty: '条款尚未发布。请稍后再看，或联系开发者。',
  },
  guidelines: {
    title: '社区规则',
    link: '社区规则',
    subtitle: '使用社区前请阅读。管理员可在后台条款页更新中英内容。',
    empty: '社区规则尚未发布。请稍后再看，或联系开发者。',
  },
  home: {
    eyebrow: '玩家平台',
    title: '记录每一次通关、每一秒进步',
    subtitle: '成就、速通、积分与社区，Medaka Games 玩家的共同记录。',
    ctaPrimary: '创建账号',
    ctaSecondary: '查看排行榜',
    ctaSignedIn: '进入我的主页',
    statPlayers: '注册玩家',
    statAchievements: '已解锁成就',
    statRuns: '审核通过的速通',
    statPoints: '已发放积分',
    topPlayers: '积分榜前列',
    fastestRuns: '最快速通',
    latestPosts: '社区动态',
    featureAchievementsTitle: '成就系统',
    featureAchievementsBody: '普通成就与特殊成就分开计算，隐藏成就在解锁前不会透露内容。',
    featureSpeedrunTitle: '速通与审核',
    featureSpeedrunBody: '按平台、版本与规则提交成绩，人工审核通过后才会进入正式排行榜。',
    featurePointsTitle: '积分流水',
    featurePointsBody: '每一笔积分都有来源与原因，历史记录不会因为规则调整而改变。',
    featureCommunityTitle: '玩家社区',
    featureCommunityBody: '分享路线与发现，关注同好，一起把记录往前推一点。',
    announcementLabel: '公告',
  },
  auth: {
    loginTitle: '欢迎回来',
    loginSubtitle: '登录后查看你的成就、速通与积分。',
    registerTitle: '创建账号',
    registerSubtitle: '加入 Medaka Games Reward，开始记录你的成绩。',
    continue: '继续',
    createAccount: '创建账户',
    start: '开始',
    finish: '完成',
    skip: '跳过',
    passwordTitle: '设置密码',
    passwordSubtitle: '设置登录密码，并阅读社区规则与隐私条款。',
    welcomeTitle: '欢迎加入',
    welcomeSubtitle: '账号即将就绪。接下来设置你的个人信息。',
    personalInfoTitle: '接下来让我们设置您的个人信息',
    personalIntroTitle: '个人简介',
    personalIntroSubtitle: '可选。头像、简介和社交链接都可以以后再填。',
    emailVerifiedContinue: '邮箱已验证，请设置密码。',
    accountReady: '账号已创建',
    nicknameRequired: '请填写昵称',
    genderRequired: '请选择性别',
    nationalityRequired: '请选择国籍',
    gender: '性别',
    genderPlaceholder: '选择性别',
    genderMale: '男性',
    genderFemale: '女性',
    genderOther: '其他',
    genderUnspecified: '不愿透露',
    birthday: '生日',
    acceptTermsPrefix: '我已阅读并同意',
    communityRules: '社区规则',
    privacyPolicy: '隐私条款',
    profileIncompleteToast: '您还没有更新您的帐户信息，请更新。',
    identifier: '用户名、邮箱或 Player ID',
    identifierPlaceholder: 'you@example.com',
    username: '用户名',
    usernameHint: '3–32 位字母、数字或下划线，注册后不可改。',
    nickname: '昵称',
    nicknameHint: '昵称可以重复，Player ID 唯一且由系统生成。',
    email: '邮箱',
    password: '密码',
    passwordHint: '至少 8 个字符，需同时包含字母和数字。',
    confirmPassword: '确认密码',
    country: '国籍',
    countryPlaceholder: '选择国家',
    forgotPassword: '忘记密码？',
    noAccount: '还没有账号？',
    haveAccount: '已经有账号了？',
    registrationClosed: '网站注册暂时关闭',
    registrationClosedBody: '管理员当前关闭了注册功能，请稍后再试。',
    agreeTerms: '注册即代表同意<termsBtn>条款</termsBtn>',
    agreement: '注册即表示你同意遵守社区规则。',
    acceptTerms: '我已阅读并同意<rulesBtn>社区规则</rulesBtn>',
    demoHint: '演示账号：{{identifier}} / {{password}}',
    forgotTitle: '找回密码',
    forgotSubtitle: '输入注册邮箱，我们会发送 6 位验证码。用验证码设置新密码。',
    forgotEmpty: '开发者还没有填写联系方式。请稍后再试，或通过游戏内公告联系。',
    contactCopied: '已复制联系方式',
    contactCopyFailed: '复制失败，请手动选择文字。',
    copyContact: '复制',
    remember: '记住我',
    passwordMismatch: '两次输入的密码不一致',
    verifyTitle: '验证邮箱',
    verifySubtitle: '我们已向 {{email}} 发送 6 位验证码，验证后才能登录。',
    verifyCode: '验证码',
    verifySubmit: '验证',
    sendCode: '发送验证码',
    resendCode: '重新发送',
    sliderTitle: '拖一下拼图',
    sliderSubtitle: '挡住刷验证码的机器人。拖到缺口附近松开就行，不用完全对齐。',
    sliderHint: '按住滑块或拼图块，拖到缺口附近松手。',
    sliderTrack: '向右滑动填充拼图',
    sliderRefresh: '换一张',
    sliderRequired: '发验证码前要先完成拼图。',
    codeSent: '验证码已成功发送，请查收。如果没有查收，请检查垃圾邮件',
    forgotIfRegistered: '如果该邮箱已注册，我们会发送验证码。',
    haveCode: '已有验证码？',
    emailNotVerified: '请先验证邮箱后再登录。',
    invalidCredentials: '账号或密码错误',
    mailUnavailable: '邮件服务暂不可用，请稍后再试或联系管理员。',
    resetSubmit: '设置新密码',
    resetDone: '密码已重置，请使用新密码登录。',
    verified: '邮箱已验证，请登录。',
    cannotVerify: '无法验证？',
    cannotVerifyTitle: '无法验证邮箱',
    cannotVerifySubtitle:
      '如果收不到验证码（例如注册时填了错误邮箱），请用下面的方式联系站长，由站长帮你处理。不要反复往错误邮箱发验证码。',
    cannotVerifyEmpty: '站长还没有填写联系方式。请稍后再试，或通过游戏内公告联系。',
    gameTicketPending: '正在把这次登录同步到游戏…请先在网页里登录。',
    gameTicketBound: '已同步到游戏，可以回到游戏里了。',
    gameTicketError: '同步到游戏失败，请在游戏里重新打开奖励站点。',
  },
  profile: {
    joined: '{{date}} 加入',
    lastActive: '最近活跃 {{time}}',
    followers: '粉丝',
    following: '关注',
    achievements: '成就',
    speedruns: '速通',
    points: '积分',
    follow: '关注',
    unfollow: '取消关注',
    following_state: '已关注',
    editProfile: '编辑资料',
    playerId: 'Player ID',
    noBio: '这位玩家还没有写简介。',
    tabOverview: '概览',
    tabAchievements: '成就',
    tabSpeedruns: '速通',
    tabPosts: '帖子',
    recentAchievements: '最近解锁',
    bestRuns: '最好成绩',
    pointsBreakdown: '积分来源',
    editTitle: '编辑个人资料',
    editSubtitle: '这些信息会显示在你的公开主页上。',
    bio: '个人简介',
    bioPlaceholder: '介绍一下你自己，或者你最想打破的记录。',
    bioCounter: '{{count}} / 280',
    avatar: '头像',
    avatarChange: '更换头像',
    avatarRemove: '恢复默认',
    avatarHint: 'PNG 或 JPG，建议 512×512，不超过 2 MB。',
    cropTitle: '裁剪头像',
    cropHint: '拖动正方形选区，确定后才会保存。',
    avatarUploadClosed: '头像上传当前已关闭。',
    socialLinks: '社交链接',
    socialLinksHint: '最多 5 条。填写网址后会自动识别平台图标。',
    socialAdd: '添加链接',
    socialUrlPlaceholder: 'https://',
    socialInvalid: '请输入有效的网址',
    socialMax: '最多 5 条社交链接',
    socialCounter: '{{count}} / {{max}}',
    socialLinkNamed: '{{platform}}',
    profileUpdated: '资料已更新',
    groups: '用户组',
    noAchievementsYet: '还没有解锁任何成就。',
    noRunsYet: '还没有通过审核的速通成绩。',
    noPostsYet: '还没有发布过帖子。',
    followersTitle: '{{name}} 的粉丝',
    followingTitle: '{{name}} 关注的玩家',
    notFound: '找不到这位玩家',
    notFoundBody: '这个 Player ID 不存在，或者账号已经被删除。',
  },
  achievements: {
    title: '成就',
    subtitle: '共 {{total}} 个成就，你已解锁 {{unlocked}} 个。',
    publicSubtitle: '共 {{total}} 个成就。登录后可以看到自己的解锁进度。',
    progress: '完成度',
    unlocked: '已解锁',
    locked: '未解锁',
    hidden: '隐藏成就',
    hiddenBody: '达成条件后才会揭晓。',
    special: '特殊成就',
    normal: '普通成就',
    unlockedAt: '{{date}} 解锁',
    unlockRate: '{{rate}}% 的玩家已获得',
    filterAll: '全部',
    filterUnlocked: '已解锁',
    filterLocked: '未解锁',
    filterSpecial: '特殊',
    filterHidden: '隐藏',
    category: '分类',
    categories: {
      story: '剧情',
      collection: '收集',
      challenge: '挑战',
      exploration: '探索',
      speedrun: '速通',
      community: '社区',
      account: '账号',
      secret: '秘密',
    },
    empty: '没有符合条件的成就。',
    pointsFromAchievements: '成就积分',
    detailTitle: '成就详情',
    unlockCount: '{{count}} 位玩家已获得',
    recentUnlockers: '最近解锁',
  },
  speedruns: {
    title: '我的速通',
    subtitle: '提交成绩、查看审核状态与获得的积分。',
    submitTitle: '提交速通成绩',
    submitSubtitle: '不需要上传视频或截图，服务器不会保存录像。审核由管理员人工完成。',
    platform: '游戏平台',
    version: '游戏版本',
    game: '游戏',
    category: '分类',
    rule: '速通规则',
    detailTitle: '速通成绩',
    duration: '用时',
    durationHint: '按 时:分:秒.毫秒 填写，例如 00:29:12.450。',
    note: '备注',
    notePlaceholder: '路线、失误或其他想让审核看到的信息（可选）。',
    submitAction: '提交审核',
    submitClosed: '速通提交当前已关闭',
    submitClosedBody: '管理员暂时关闭了速通提交，请稍后再试。',
    submitted: '成绩已提交，等待审核',
    tierReached: '达到档位',
    tierNone: '未达到任何档位',
    tierPreview: '按当前用时，这次提交对应 {{tier}}（{{points}} 分）。',
    tierPreviewNone: '当前用时未达到任何积分档位，但成绩仍会被记录。',
    status: {
      pending: '审核中',
      approved: '已通过',
      rejected: '已拒绝',
      revoked: '已撤销',
      withdrawn: '已撤回',
    },
    statusHint: {
      pending: '管理员会尽快处理，结果会通过通知告知你。',
      approved: '成绩已进入正式排行榜。',
      rejected: '这次提交没有通过审核。',
      revoked: '这条成绩在复核后被撤销，相应积分已扣回，记录会保留。',
      withdrawn: '你已撤回这次提交。',
    },
    filterStatus: '审核状态',
    submittedAt: '提交时间',
    reviewedAt: '审核时间',
    reviewNote: '审核意见',
    playerNote: '玩家备注',
    awardedPoints: '获得积分',
    rules: '规则说明',
    tiers: '积分档位',
    tierUnder: '{{time}} 以内',
    empty: '你还没有提交过速通成绩。',
    emptyCta: '提交第一次成绩',
    rulesTitle: '速通规则与档位',
    onlyApproved: '正式排行榜只显示审核通过的成绩。',
    playedAt: '通关日期',
    proofUrl: '证明链接',
    proofHint: '可选。只接受 http(s) 链接，服务器不会保存视频。',
    withdraw: '撤回提交',
    withdrawnOk: '已撤回这次提交。',
    emailNotVerified: '请先验证邮箱后再提交速通。',
  },
  points: {
    title: '我的积分',
    subtitle: '积分来自成就、速通奖励与管理员调整，每一笔都有记录。',
    balance: '当前积分',
    rank: '积分排名',
    rankOf: '共 {{total}} 位玩家',
    earned30: '近 30 天获得',
    ledger: '积分流水',
    ledgerEmpty: '还没有积分记录。',
    source: '来源',
    sources: {
      achievement: '成就',
      speedrun: '速通',
      admin_adjust: '管理员',
      reversal: '撤销扣回',
      system: '系统',
      other: '其他',
    },
    totalDeducted: '累计扣除',
    totalEarnedHint: '累计获得 {{total}}',
    reason: '原因',
    balanceAfter: '变动后余额',
    date: '时间',
    change: '变动',
    breakdown: '构成',
    historyNote: '已发放的积分不会因为奖励规则调整而改变；成绩被撤销时会扣回积分，但流水会完整保留。',
  },
  leaderboard: {
    title: '排行榜',
    subtitle: '积分榜与速通榜。排行榜只显示公开信息。',
    tabPoints: '积分榜',
    tabSpeedruns: '速通榜',
    pointsTitle: '总积分排行榜',
    speedrunsTitle: '速通排行榜',
    rank: '#',
    player: '玩家',
    points: '积分',
    achievements: '成就',
    trend: '变化',
    time: '用时',
    platform: '平台',
    version: '版本',
    game: '游戏',
    category: '分类',
    pickCategory: '请选择一个速通分类以查看排行榜。',
    noCategories: '这个游戏还没有速通规则。',
    rule: '规则',
    tier: '档位',
    achievedAt: '达成时间',
    myRank: '我的排名',
    myRankNone: '你还没有进入这个排行榜。',
    myRankHint: '登录后可以看到自己的排名。',
    filters: '筛选条件',
    allPlatforms: '全部平台',
    allVersions: '全部版本',
    allRules: '全部规则',
    approvedOnly: '仅显示审核通过的成绩',
    empty: '没有符合条件的记录。',
    perPlayerNote: '每位玩家只显示其最好成绩。',
  },
  community: {
    title: '玩家社区',
    subtitle: '分享路线、讨论发现、认识同好。',
    newPost: '发布帖子',
    newPostTitle: '发布新帖子',
    postTitle: '标题',
    postTitlePlaceholder: '一句话说清楚你要分享什么',
    postBody: '正文',
    postBodyPlaceholder: '支持换行。请不要发布无关内容或广告。',
    tags: '标签',
    board: '板块',
    allBoards: '全部板块',
    tagsHint: '最多 5 个，用回车分隔。',
    publish: '发布',
    published: '帖子已发布',
    sortRecent: '最新',
    sortPopular: '最多赞',
    sortDiscussed: '最多评论',
    pinned: '置顶',
    comments: '评论',
    commentCount: '{{count}} 条评论',
    likeCount: '{{count}} 个赞',
    addComment: '写评论',
    commentPlaceholder: '友善地参与讨论。',
    comment: '评论',
    commentPublished: '评论已发布',
    reply: '回复',
    replyTo: '回复 {{name}}',
    noComments: '还没有评论，来说第一句。',
    like: '赞',
    liked: '已赞',
    report: '举报',
    reportTitle: '举报内容',
    reportSubtitle: '管理员会审核你的举报，请不要重复提交。',
    reportReason: '举报原因',
    reportDetail: '补充说明',
    reportDetailPlaceholder: '可以补充具体情况（可选）。',
    reportSubmitted: '举报已提交，感谢你的反馈。',
    reasons: {
      spam: '垃圾信息或广告',
      abuse: '辱骂或人身攻击',
      cheating: '作弊或虚假成绩',
      nsfw: '不适宜内容',
      off_topic: '跑题',
      other: '其他',
    },
    closed: '社区暂时关闭',
    closedBody: '管理员当前关闭了社区功能，请稍后再试。',
    signInToPost: '登录后才能发帖、评论和点赞。',
    empty: '还没有帖子。',
    deletePostConfirm: '确定要删除这篇帖子吗？此操作无法撤销。',
    postDeleted: '帖子已删除',
    searchPlaceholder: '搜索帖子',
    byAuthor: '作者',
    readMore: '阅读全文',
    notFound: '找不到这篇帖子',
    notFoundBody: '帖子可能已被删除，或者链接不正确。',
  },
  notifications: {
    title: '通知',
    subtitle: '成就、速通审核、积分与社区的最新消息。',
    markAllRead: '全部标为已读',
    markRead: '标为已读',
    allRead: '已全部标为已读',
    unread: '未读',
    filterAll: '全部',
    filterUnread: '未读',
    empty: '没有通知。',
    emptyUnread: '没有未读通知。',
    unreadBadge: '{{count}} 条未读',
    kinds: {
      achievement: '成就',
      speedrun: '速通',
      points: '积分',
      account: '账号',
      community: '社区',
      follow: '关注',
      admin: '管理员',
    },
    types: {
      achievement_unlocked: '成就',
      achievement_revoked: '成就',
      speedrun_approved: '速通',
      speedrun_rejected: '速通',
      speedrun_revoked: '速通',
      points_adjusted: '积分',
      account_banned: '账号',
      account_unbanned: '账号',
      account_updated: '账号',
      post_commented: '社区',
      post_liked: '社区',
      comment_replied: '社区',
      new_follower: '关注',
      report_resolved: '社区',
      admin_message: '管理员',
      system: '系统',
    },
  },
  settings: {
    title: '账号设置',
    subtitle: '管理你的资料、偏好与安全设置。',
    tabProfile: '个人资料',
    tabPreferences: '偏好',
    tabSecurity: '安全',
    preferencesTitle: '界面偏好',
    preferencesSubtitle: '语言与主题会保存在这台设备上。',
    securityTitle: '密码与登录',
    changePassword: '修改密码',
    currentPassword: '当前密码',
    newPassword: '新密码',
    passwordChanged: '密码已修改',
    sessions: '登录设备',
    sessionsSubtitle: '包含游戏客户端的登录记录。',
    currentSession: '当前设备',
    revokeSession: '注销',
    sessionRevoked: '该设备已注销',
    lastUsed: '最近使用 {{time}}',
    accountTitle: '账号信息',
    email: '邮箱',
    emailNone: '未绑定',
    emailVerified: '已验证',
    emailUnverified: '未验证',
    emailUnbound: '未绑定',
    bindEmail: '绑定邮箱',
    bindEmailHint: '这个账号还没有邮箱。发送验证码到新地址后再提交。',
    bindEmailDone: '邮箱已绑定。',
    verifyCurrentEmail: '验证当前邮箱',
    verifyCurrentEmailHint: '这个邮箱还没验证。发送验证码到当前地址后再提交。',
    verifyEmailDone: '邮箱已验证。',
    bindEmailCodeSent: '验证码已成功发送，请查收。如果没有查收，请检查垃圾邮件',
    verifyEmailCodeSent: '验证码已成功发送，请查收。如果没有查收，请检查垃圾邮件',
    playerIdHint: 'Player ID 是你的唯一标识，不能修改。',
    dangerTitle: '危险操作',
    dangerBody: '删除账号会软删除你的资料。成就、速通、积分与审计记录会保留。',
    dangerConfirm: '输入 DELETE 以确认',
    deleteAccount: '删除账号',
    deleteAccountDone: '账号已删除',
    changeEmail: '更换邮箱',
    newEmail: '新邮箱',
    emailChanged: '邮箱已更新。',
    emailCode: '邮箱验证码',
    sendEmailCode: '发送验证码',
    emailCodeSent: '验证码已成功发送，请查收。如果没有查收，请检查垃圾邮件',
    confirmPassword: '确认新密码',
    revokeOthers: '注销其他会话',
    othersRevoked: '其他会话已注销',
    gameTokens: '游戏客户端登录',
    gameTokensSubtitle: '撤销后需要在游戏里重新登录。',
    revokeToken: '撤销',
    tokenRevoked: '已撤销该游戏登录',
    device: '绑定设备',
    deviceNone: '当前没有绑定游戏设备。',
    deviceBound: '已绑定',
    username: '用户名',
  },
  errors: {
    title: '出了一点问题',
    generic: '请求失败，请稍后再试。',
    network: '网络连接失败，请检查网络后重试。',
    unauthorized: '请先登录。',
    forbidden: '你没有权限执行这个操作。',
    notFound: '找不到这个页面',
    notFoundBody: '链接可能已经失效，或者页面被移动了。',
    validation: '请检查表单中标出的问题。',
    rateLimited: '操作太频繁，请 {{seconds}} 秒后再试。',
    rateLimitedShort: '操作太频繁，请稍后再试。',
    banned: '账号已被封禁',
    bannedBody: '你的账号目前被封禁，无法执行这个操作。',
    bannedReason: '封禁原因：{{reason}}',
    bannedUntil: '解封时间：{{date}}',
    bannedPermanent: '这是永久封禁。',
    maintenance: '网站维护中',
    maintenanceBody: '我们正在进行维护，请稍后再访问。',
    featureDisabled: '这个功能当前已关闭。',
    goHome: '返回首页',
    fields: {
      required: '这一项是必填的',
      invalid: '格式不正确',
      taken: '已经被使用了',
      too_long: '内容太长了',
      too_short: '内容太短了',
      incorrect: '不正确',
      implausible: '这个用时看起来不合理',
      blocked_word: '包含不允许的内容',
      invalid_type: '文件格式不支持',
    },
  },
  ban: {
    banner: '你的账号目前被封禁，只能浏览内容。',
  },
} as const;

/**
 * Widens every leaf from its literal type to `string` while keeping the key
 * structure exact, so translations must match the shape but not the text.
 */
type Widen<T> = T extends string ? string : { [K in keyof T]: Widen<T[K]> };

export type Dictionary = Widen<typeof zh>;
